import React, { useEffect, useMemo, useState } from "react";
import { Button, DatePicker, Radio, Table, message } from "antd";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { formatCents } from "../shared/money";
import Amount from "../shared/Amount";
import PageToolbar from "../../../../design-system/PageToolbar";
import ResponsiveTable from "../../../../design-system/ResponsiveTable";
import { errorText, openBlob } from "../api";
import * as api from "./api";

const SusaTab = () => {
  const { t } = useTranslation("accounting");
  const [mode, setMode] = useState("month");
  const [month, setMonth] = useState(dayjs().subtract(1, "month"));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const params = useMemo(
    () => (mode === "month" ? { period: month.format("YYYY-MM") } : { year: month.year() }),
    [mode, month]
  );

  useEffect(() => {
    setLoading(true);
    api
      .getSusa(params)
      .then(res => setData(res.data))
      .catch(error => message.error(errorText(error)))
      .finally(() => setLoading(false));
  }, [params]);

  const side = (v, want) => {
    const text = want === "S" ? (v > 0 ? formatCents(v) : "") : v < 0 ? formatCents(-v) : "";
    return text ? <span className="acct-num">{text}</span> : "";
  };

  return (
    <div>
      <PageToolbar
        filters={
          <>
            <Radio.Group
              value={mode}
              onChange={e => setMode(e.target.value)}
              options={[
                { value: "month", label: t("Month") },
                { value: "year", label: t("Year") }
              ]}
              optionType="button"
            />
            <DatePicker
              picker={mode === "month" ? "month" : "year"}
              value={month}
              onChange={v => v && setMonth(v)}
            />
          </>
        }
        actions={
          <Button
            onClick={() =>
              openBlob(api.downloadSusa(params), `SuSa_${data?.from}_${data?.to}.csv`).catch(e =>
                message.error(errorText(e))
              )
            }
          >
            {t("Download CSV (DATEV format)")}
          </Button>
        }
      />
      <ResponsiveTable
        mobileLayout="table"
        className="acct-grid"
        size="small"
        loading={loading}
        pagination={false}
        rowKey="account"
        dataSource={data?.rows || []}
        summary={() =>
          data ? (
            <Table.Summary.Row>
              <Table.Summary.Cell index={0} colSpan={2}>
                <strong>{t("Total")}</strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={2} align="end">
                <Amount cents={data.totals.opening} strong />
              </Table.Summary.Cell>
              <Table.Summary.Cell index={3} align="end">
                <Amount cents={data.totals.debit} strong />
              </Table.Summary.Cell>
              <Table.Summary.Cell index={4} align="end">
                <Amount cents={data.totals.credit} strong />
              </Table.Summary.Cell>
              <Table.Summary.Cell index={5} align="end">
                <strong>{side(data.totals.closing, "S")}</strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={6} align="end">
                <strong>{side(data.totals.closing, "H")}</strong>
              </Table.Summary.Cell>
            </Table.Summary.Row>
          ) : null
        }
        columns={[
          { title: t("Account"), dataIndex: "account", width: 90 },
          { title: t("Name"), dataIndex: "name" },
          {
            title: t("Opening balance"),
            dataIndex: "opening",
            align: "end",
            render: v => <Amount cents={v} />
          },
          {
            title: t("Debit"),
            dataIndex: "debit",
            align: "end",
            render: v => <Amount cents={v} />
          },
          {
            title: t("Credit"),
            dataIndex: "credit",
            align: "end",
            render: v => <Amount cents={v} />
          },
          {
            title: t("Balance debit"),
            dataIndex: "closing",
            align: "end",
            render: v => side(v, "S")
          },
          {
            title: t("Balance credit"),
            dataIndex: "closing",
            align: "end",
            render: v => side(v, "H")
          }
        ]}
      />
    </div>
  );
};

export default SusaTab;
